/**
 * Quirks — the app's personality layer.
 *
 * `mumble()` returns a random coach-voice line for loading/empty states.
 * `athleteTitle()` maps a streak to a fun nickname shown next to the athlete's name.
 * `fireConfetti()` bursts colored particles from the click point — pure CSS,
 *   no external library.
 * `useKonami()` listens for the Konami code sequence and calls the callback.
 */
import { useEffect } from "react";

const MUMBLES = [
  "Warming up your splits",
  "Consulting the coach",
  "Checking last night's kip",
  "Weighing the porridge",
  "Counting your carbs, slowly",
  "Chalking up the numbers",
  "Loading the tempo playlist",
  "Filing your recovery",
  "Tightening the laces",
  "Reading the wind",
  "Fetching yesterday's grit",
  "Waking the fuelling engine",
];

export function mumble() {
  return MUMBLES[Math.floor(Math.random() * MUMBLES.length)] + "…";
}

/** Auto-earned nickname based on training + logging behaviour. */
export function athleteTitle({ streak = 0, sessions = 0, macroHitRate = 0 } = {}) {
  if (streak >= 60 && macroHitRate >= 0.7) return { title: "Nutrition Assassin", color: "#E2E30A", desc: "60+ day streak · hitting macros 70%+ of days" };
  if (streak >= 30) return { title: "Machine", color: "#34C759", desc: "30+ day logging streak — unstoppable" };
  if (streak >= 14) return { title: "Cog", color: "#4C9AFF", desc: "14+ day streak — running like clockwork" };
  if (streak >= 7) return { title: "On the roll", color: "#FF9500", desc: "7+ day streak — momentum building" };
  if (sessions >= 50) return { title: "Half-Century Club", color: "#FF6B57", desc: "50+ sessions logged all-time" };
  return null;
}

/**
 * CSS-only confetti burst. Spawns short-lived colored particles anchored to
 * the given (x, y) or the window centre. Zero deps, ~1KB.
 */
export function fireConfetti({ x = null, y = null, count = 60 } = {}) {
  const cx = x ?? window.innerWidth / 2;
  const cy = y ?? window.innerHeight / 3;
  const colors = ["#E2E30A", "#34C759", "#FF9500", "#4C9AFF", "#FF6B57", "#8BE04E"];
  const layer = document.createElement("div");
  layer.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
  document.body.appendChild(layer);
  for (let i = 0; i < count; i++) {
    const p = document.createElement("div");
    const size = 6 + Math.random() * 6;
    const dx = (Math.random() - 0.5) * 500;
    const dy = -Math.random() * 400 - 100;
    const rot = Math.random() * 720 - 360;
    p.style.cssText = `position:absolute;left:${cx}px;top:${cy}px;width:${size}px;height:${size * 0.4}px;background:${colors[i % colors.length]};transform:translate(-50%,-50%) rotate(0deg);opacity:1;transition:all ${900 + Math.random() * 700}ms cubic-bezier(.2,.65,.4,1)`;
    layer.appendChild(p);
    requestAnimationFrame(() => {
      p.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 700}px)) rotate(${rot}deg)`;
      p.style.opacity = "0";
    });
  }
  setTimeout(() => layer.remove(), 1800);
}

/**
 * Fires confetti + toast when a streak hits a milestone (3, 7, 30, 100 days).
 * Uses localStorage to only celebrate each milestone once per athlete.
 */
export function celebrateStreakMilestone(streak, email, toastFn) {
  const milestones = [3, 7, 30, 100];
  if (!milestones.includes(streak)) return;
  const key = `spc:streak-celebrated:${email}:${streak}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, "1");
  fireConfetti();
  const msgs = {
    3: "3-day streak — the habit's forming",
    7: "One full week — that's how compounding starts",
    30: "30 days on the trot — you're a Machine",
    100: "100 days — SPC hall of fame material",
  };
  toastFn?.(msgs[streak], { duration: 6000 });
}

/** Konami code detector: ↑↑↓↓←→←→BA. Calls onUnlock when the full sequence hits. */
const KONAMI = ["ArrowUp","ArrowUp","ArrowDown","ArrowDown","ArrowLeft","ArrowRight","ArrowLeft","ArrowRight","b","a"];

export function useKonami(onUnlock) {
  useEffect(() => {
    let idx = 0;
    const onKey = (e) => {
      // Browser autofill / IME / password-manager sometimes dispatches
      // keydown events with no `key` field. Guard so we don't blow up
      // the whole page — the login screen used to crash with:
      // "Cannot read properties of undefined (reading 'length')".
      if (!e || typeof e.key !== "string") return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (k === KONAMI[idx]) {
        idx += 1;
        if (idx === KONAMI.length) { idx = 0; onUnlock?.(); }
      } else {
        idx = k === KONAMI[0] ? 1 : 0;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onUnlock]);
}
